/* eslint-disable no-nested-ternary */
import React, { SetStateAction, useRef } from 'react';
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { ICase } from '../../../../services/cases/types/ICase';
import { UserProfile } from '../../../../services/identity/types/UserProfile';
import { updateOneReport } from '../../../../services/oneReport/updateOneReport';
import { checkCollapsibleHeaderColor } from '../../../../Utils/getCollapsibleHeader';
import { OralHealthReportSubmit } from '../../assets/types';
import styles from '../../styles.module.scss';

const isMediaUtilizedOptions = ['Utilized', 'Not utilized'];
const objectiveOralOptions = ['Visualized', 'Not visualized'];

type ObjectiveAttributeName = 'is_media_utilized' | 'is_intra_oral_utilized' | 'is_extra_oral_utilized';

type ObjectiveSaveState = {
  inFlight: boolean;
  latestRequestSeq: number;
  pendingValue?: string;
  pendingRequestSeq?: number;
};

type ObjectiveSectionProps = {
  consultationForm: OralHealthReportSubmit;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  thisCase: ICase;
  setCase: React.Dispatch<SetStateAction<ICase>>;
  userInfo: UserProfile;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
};

type ObjectiveFieldProps = {
  title: string;
  name: 'objective.is_media_utilized' | 'objective.is_intra_oral_utilized' | 'objective.is_extra_oral_utilized';
  attributeName: ObjectiveAttributeName;
  value?: string;
  options: string[];
  editable: boolean;
  register: UseFormRegister<OralHealthReportSubmit>;
  setValue: UseFormSetValue<OralHealthReportSubmit>;
  trigger: UseFormTrigger<OralHealthReportSubmit>;
  errors: FieldErrors<OralHealthReportSubmit>;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  saveOneReportObjective: (attributeName: ObjectiveAttributeName, attributeValue: string) => Promise<void>;
};

const createObjectiveSaveState = (): Record<ObjectiveAttributeName, ObjectiveSaveState> => ({
  is_media_utilized: { inFlight: false, latestRequestSeq: 0 },
  is_intra_oral_utilized: { inFlight: false, latestRequestSeq: 0 },
  is_extra_oral_utilized: { inFlight: false, latestRequestSeq: 0 },
});

const isObjectivePositiveValue = (attributeValue: string): boolean =>
  attributeValue === 'Utilized' || attributeValue === 'Visualized';

const ObjectiveField = ({
  title,
  name,
  attributeName,
  value,
  options,
  editable,
  setValue,
  trigger,
  errors,
  setConsultationForm,
  saveOneReportObjective,
}: ObjectiveFieldProps): JSX.Element => {
  const fieldError =
    name === 'objective.is_media_utilized'
      ? errors?.objective?.is_media_utilized?.message
      : name === 'objective.is_intra_oral_utilized'
      ? errors?.objective?.is_intra_oral_utilized?.message
      : errors?.objective?.is_extra_oral_utilized?.message;

  const errorMessage = typeof fieldError === 'string' ? fieldError : '';
  const hasError = Boolean(errorMessage);

  const updateObjectiveState = (selectedValue: string) => {
    setConsultationForm((prev) => ({
      ...prev,
      objective: {
        ...prev.objective,
        [attributeName]: selectedValue,
      },
    }));
  };

  return (
    <div className={styles.objectiveRow}>
      <div className={styles.objectiveLabel}>
        {title}
        <span className={styles.requiredMark}>*</span>
      </div>

      <div className={styles.objectiveFieldGroup}>
        <div className={`${styles.objectiveOptions} ${hasError ? styles.objectiveOptionsError : ''}`}>
          {options.map((option) => {
            const id = `${attributeName}_${option.replace(/\s+/g, '_').toLowerCase()}`;

            return editable ? (
              <label key={option} htmlFor={id} className={styles.objectiveOption}>
                <input
                  id={id}
                  name={name}
                  type="radio"
                  value={option}
                  checked={value === option}
                  className={styles.objectiveRadio}
                  onChange={(e) => {
                    const selectedValue = e.target.value;

                    updateObjectiveState(selectedValue);
                    setValue(name, selectedValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });

                    void saveOneReportObjective(attributeName, selectedValue);
                    void trigger(name);
                  }}
                />
                <span className={styles.objectiveOptionText}>{option}</span>
              </label>
            ) : (
              <label key={option} htmlFor={id} className={styles.objectiveOptionLabel}>
                <input
                  id={id}
                  name={name}
                  type="radio"
                  value={option}
                  checked={value === option}
                  className={styles.objectiveRadio}
                  disabled={true}
                />
                <span className={styles.objectiveOptionText}>{option}</span>
              </label>
            );
          })}
        </div>

        {hasError && <div className={styles.sectionErrorText}>{errorMessage}</div>}
      </div>
    </div>
  );
};

const ObjectiveSection = ({
  consultationForm,
  setConsultationForm,
  thisCase,
  setCase,
  userInfo,
  register,
  setValue,
  trigger,
  errors,
}: ObjectiveSectionProps): JSX.Element => {
  const sectionMode = checkCollapsibleHeaderColor(thisCase || '', userInfo, 'Assessment');
  const editable = sectionMode === 'edit' || sectionMode === 'editNotRequired';
  const objectiveSaveStateRef = useRef<Record<ObjectiveAttributeName, ObjectiveSaveState>>(createObjectiveSaveState());

  const flushObjectiveSave = async (attributeName: ObjectiveAttributeName): Promise<void> => {
    const saveState = objectiveSaveStateRef.current[attributeName];

    if (saveState.inFlight || saveState.pendingValue === undefined || saveState.pendingRequestSeq === undefined) {
      return;
    }

    const attributeValue = saveState.pendingValue;
    const requestSeq = saveState.pendingRequestSeq;
    const normalizedValue = isObjectivePositiveValue(attributeValue);
    const updateData: Partial<Record<ObjectiveAttributeName, boolean>> = {
      [attributeName]: normalizedValue,
    };

    saveState.pendingValue = undefined;
    saveState.pendingRequestSeq = undefined;
    saveState.inFlight = true;

    try {
      await updateOneReport(thisCase?.one_report.id || '', updateData, userInfo.role.name);

      if (saveState.latestRequestSeq === requestSeq) {
        setCase((prevCase) => ({
          ...prevCase,
          one_report: {
            ...prevCase.one_report,
            [attributeName]: normalizedValue,
          },
        }));
      }
    } finally {
      saveState.inFlight = false;

      if (saveState.pendingValue !== undefined) {
        void flushObjectiveSave(attributeName);
      }
    }
  };

  const saveOneReportObjective = async (
    attributeName: ObjectiveAttributeName,
    attributeValue: string,
  ): Promise<void> => {
    const saveState = objectiveSaveStateRef.current[attributeName];
    const requestSeq = saveState.latestRequestSeq + 1;

    saveState.latestRequestSeq = requestSeq;
    saveState.pendingValue = attributeValue;
    saveState.pendingRequestSeq = requestSeq;

    await flushObjectiveSave(attributeName);
  };

  return (
    <div className={styles.objectiveSectionWrapper}>
      <h3 className={styles.objectiveTitle}>Objective</h3>

      <ObjectiveField
        title="Photos, Video, Radiographs"
        name="objective.is_media_utilized"
        attributeName="is_media_utilized"
        value={consultationForm?.objective?.is_media_utilized}
        options={isMediaUtilizedOptions}
        editable={editable}
        register={register}
        setValue={setValue}
        trigger={trigger}
        errors={errors}
        setConsultationForm={setConsultationForm}
        saveOneReportObjective={saveOneReportObjective}
      />

      <ObjectiveField
        title="Intra-Oral"
        name="objective.is_intra_oral_utilized"
        attributeName="is_intra_oral_utilized"
        value={consultationForm?.objective?.is_intra_oral_utilized}
        options={objectiveOralOptions}
        editable={editable}
        register={register}
        setValue={setValue}
        trigger={trigger}
        errors={errors}
        setConsultationForm={setConsultationForm}
        saveOneReportObjective={saveOneReportObjective}
      />

      <ObjectiveField
        title="Extra-Oral"
        name="objective.is_extra_oral_utilized"
        attributeName="is_extra_oral_utilized"
        value={consultationForm?.objective?.is_extra_oral_utilized}
        options={objectiveOralOptions}
        editable={editable}
        register={register}
        setValue={setValue}
        trigger={trigger}
        errors={errors}
        setConsultationForm={setConsultationForm}
        saveOneReportObjective={saveOneReportObjective}
      />
    </div>
  );
};

export default React.memo(ObjectiveSection);
